// BookingPopup.jsx
// ─ Shown when the "予約する / Book Now" navbar CTA is tapped
// ─ Two options: online form (scrolls to #reservation) or direct call
// ─ Pure Tailwind, bilingual ja/en, Framer Motion entry animation
// ─ Closes on backdrop click, ✕ button, or Escape key

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const PHONE     = '0537-26-9489';
const TEL       = 'tel:0537269489';

export default function BookingPopup({ open, onClose }) {
  const { i18n } = useTranslation();
  const isJa = i18n.language?.startsWith('ja');

  /* close on Escape key */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /* lock body scroll while open */
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleOnline = () => {
    onClose();
    /* small delay so the popup closes first */
    setTimeout(() => {
      document.getElementById('reservation')?.scrollIntoView({ behavior:'smooth' });
    }, 200);
  };

  return (
    <AnimatePresence>
      {open && (
        /* BACKDROP */
        <motion.div
          className="fixed inset-0 z-[9998] flex items-center justify-center p-5"
          style={{ background:'rgba(0,0,0,.65)', backdropFilter:'blur(8px)' }}
          initial={{ opacity:0 }}
          animate={{ opacity:1 }}
          exit={{ opacity:0 }}
          transition={{ duration:.25 }}
          onClick={onClose}
        >
          {/* POPUP CARD */}
          <motion.div
            className="
              relative max-w-[480px] w-full
              bg-[#fffdf8] rounded-[28px]
              border-[2.5px] border-[#dfd1bc]
              shadow-[0_32px_80px_rgba(0,0,0,.35)]
              overflow-hidden
            "
            initial={{ scale:.88, opacity:0, y:24 }}
            animate={{ scale:1,   opacity:1, y:0  }}
            exit={{ scale:.88, opacity:0, y:16 }}
            transition={{ type:'spring', stiffness:320, damping:26 }}
            onClick={e => e.stopPropagation()}
          >
            {/* rainbow top stripe */}
            <div className="absolute top-0 left-0 right-0 h-[5px] z-10"
              style={{ background:'repeating-linear-gradient(90deg,#c8340a 0,#c8340a 40px,#1f4d2b 40px,#1f4d2b 80px,#d89c10 80px,#d89c10 120px)' }}/>

            {/* close button */}
            <button
              onClick={onClose}
              className="
                absolute top-4 right-4 z-20
                w-8 h-8 rounded-full
                bg-black/[.06] hover:bg-black/[.12]
                border-0 cursor-pointer
                flex items-center justify-center
                text-[#888] text-[16px]
                transition-colors
              "
            >✕</button>

            {/* ── HEADER ── */}
            <div className="pt-8 pb-5 px-7 text-center">
              <span className="text-[44px] block mb-3">🏕️</span>
              <h2 className="font-['Cormorant_Garamond'] font-bold text-[#173617] text-[26px] leading-tight mb-2">
                {isJa ? 'ご予約方法を選択' : 'How would you like to book?'}
              </h2>
              <p className="text-[13px] font-bold text-[#666] leading-[1.75]">
                {isJa
                  ? <>オンラインフォームまたはお電話でご予約いただけます。<br/>お支払いは当日・現地にて。</>
                  : <>Book online or call us directly.<br/>Payment collected on the day at the venue.</>}
              </p>
            </div>

            {/* ── DIVIDER ── */}
            <div className="flex items-center gap-3 px-7 mb-5">
              <div className="flex-1 h-[1.5px] bg-[#e7dbc7]"/>
              <span className="text-[10px] font-extrabold tracking-[.25em] uppercase text-[#bbb] whitespace-nowrap">
                {isJa ? '予約方法を選んでください' : 'Choose a method'}
              </span>
              <div className="flex-1 h-[1.5px] bg-[#e7dbc7]"/>
            </div>

            {/* ── OPTIONS ── */}
            <div className="flex flex-col gap-3 px-7 pb-6">

              {/* ONLINE FORM */}
              <motion.button
                onClick={handleOnline}
                whileHover={{ y:-2 }} whileTap={{ scale:.98 }}
                className="
                  w-full flex items-center gap-4 text-left
                  bg-[#1f4d2b] rounded-[18px] p-5
                  border-0 cursor-pointer
                  shadow-[0_6px_0_#0f2a0a,0_10px_24px_rgba(31,77,43,.2)]
                  relative overflow-hidden
                  transition-shadow
                "
              >
                <span className="absolute inset-0 bg-gradient-to-br from-white/[.07] to-transparent pointer-events-none"/>
                <div className="w-12 h-12 rounded-[14px] bg-white/15 flex items-center justify-center text-[22px] flex-shrink-0">
                  📅
                </div>
                <div className="flex-1 relative z-10">
                  <p className="text-[9px] font-extrabold tracking-[.22em] uppercase text-white/55 mb-1">
                    {isJa ? 'オンライン予約' : 'Online Booking'}
                  </p>
                  <p className="text-[17px] font-extrabold text-white leading-tight mb-1">
                    {isJa ? 'フォームで予約する' : 'Book via Form'}
                  </p>
                  <p className="text-[11px] font-bold text-white/60 leading-snug">
                    {isJa
                      ? '日程・体験・人数を選んで送信'
                      : 'Choose date, activity & group size'}
                  </p>
                </div>
                <span className="text-[20px] text-white/40 flex-shrink-0 relative z-10">→</span>
              </motion.button>

              {/* PHONE CALL */}
              <motion.a
                href={TEL}
                onClick={onClose}
                whileHover={{ y:-2 }} whileTap={{ scale:.98 }}
                className="
                  flex items-center gap-4 no-underline
                  bg-white rounded-[18px] p-5
                  border-[2.5px] border-[#dfd1bc]
                  shadow-[0_5px_0_#dfd1bc]
                  cursor-pointer
                  transition-shadow
                "
              >
                <div className="w-12 h-12 rounded-[14px] bg-[rgba(234,98,42,.12)] flex items-center justify-center text-[22px] flex-shrink-0">
                  📞
                </div>
                <div className="flex-1">
                  <p className="text-[9px] font-extrabold tracking-[.22em] uppercase text-[#aaa] mb-1">
                    {isJa ? '電話で予約' : 'Call to Reserve'}
                  </p>
                  <p className="font-['Cormorant_Garamond'] font-bold text-[#c8340a] text-[24px] leading-none mb-1">
                    {PHONE}
                  </p>
                  <p className="text-[11px] font-bold text-[#888] leading-snug">
                    {isJa
                      ? '営業時間 9:30〜17:00（年中無休）'
                      : 'Business hours 9:30 AM – 5:00 PM'}
                  </p>
                </div>
                <span className="text-[20px] text-[#ddd] flex-shrink-0">→</span>
              </motion.a>

            </div>

            {/* ── PAYMENT NOTE ── */}
            <p className="text-center text-[11px] font-bold text-[#aaa] pb-6 px-7 leading-[1.7]">
              💴 {isJa
                ? 'お支払いは当日・現地にて。オンライン決済なし。'
                : 'No online payment. Pay on the day at the venue.'}
            </p>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}